import {
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type DragEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { Button } from '@/components/ui/Button';
import { Tooltip } from '@/components/ui/Tooltip';
import { cx } from '@/lib/cx';
import type { LayoutWidth, PageLayout } from '@/lib/use-page-layout';

/*
 * A page's movable sections as a two-column grid each person can rearrange
 * (engine: `lib/use-page-layout.ts`; contract owner 2026-10-20).
 *
 * ── NOT EDITING = TODAY'S PAGE ──────────────────────────────────────────────
 *
 * Each visible block is one bare grid item (`min-w-0`, `lg:col-span-2` when
 * full), so a page whose defaults are all `full` renders exactly the gap stack
 * it had before. Hidden blocks, ids with no `render` entry, and renders that
 * return null/false/undefined are not rendered at all — in BOTH modes, so a
 * section the person's permission set hides never shows up as an empty frame
 * or a tray entry while editing.
 *
 * ── EDITING ─────────────────────────────────────────────────────────────────
 *
 * Each block gets a dashed hairline frame and a header strip; its content
 * stays live underneath (no overlay). Reordering is native HTML5 drag from the
 * GRIP ONLY — dragging the whole card would fight text selection and the
 * tables' own column drags — and it is optional: Move up / Move down are real
 * buttons that always work from the keyboard. Every change is announced in a
 * polite live region, and focus is put back on the control that made it
 * (moving a focused node in the DOM blurs it in Chromium).
 *
 * `layout.move(id, toIndex)` is the block's FINAL index in `layout.blocks`
 * (remove, then insert). Up/down step over blocks that are hidden or have
 * nothing to show rather than calling `moveBy`, so a press always visibly
 * moves something.
 *
 * Full / Half only changes anything at `lg` (two columns), so the toggle is
 * not offered below it.
 */

type Block = PageLayout['blocks'][number];
type Edge = 'top' | 'bottom' | 'left' | 'right';
type Gap = 'sm' | 'md';

export interface LayoutGridProps {
  layout: PageLayout;
  render: Readonly<Record<string, () => ReactNode | null>>;
  gap?: Gap;
  className?: string;
}

const GAPS: Record<Gap, string> = {
  sm: 'gap-3',
  md: 'gap-4',
};

/*
 * The drop line sits centred in the gutter on the side the block will land:
 * half the gap + the 1px frame border + half the 2px line, measured from the
 * frame's padding edge (gap-3 → 8px, gap-4 → 10px). Spelt out in full so
 * Tailwind sees every class.
 */
const EDGES: Record<Gap, Record<Edge, string>> = {
  sm: {
    top: '-top-[8px] inset-x-2 h-0.5',
    bottom: '-bottom-[8px] inset-x-2 h-0.5',
    left: '-left-[8px] inset-y-2 w-0.5',
    right: '-right-[8px] inset-y-2 w-0.5',
  },
  md: {
    top: '-top-[10px] inset-x-2 h-0.5',
    bottom: '-bottom-[10px] inset-x-2 h-0.5',
    left: '-left-[10px] inset-y-2 w-0.5',
    right: '-right-[10px] inset-y-2 w-0.5',
  },
};

type FocusAction = 'up' | 'down' | 'width' | 'hide' | 'show';

interface PendingFocus {
  id: string;
  action: FocusAction;
}

interface Drop {
  id: string;
  edge: Edge;
}

function nodeOf(entry: (() => ReactNode | null) | undefined): ReactNode | null {
  if (!entry) return null;
  const node = entry();
  return node === undefined || node === null || typeof node === 'boolean' ? null : node;
}

function escapeId(id: string): string {
  return typeof CSS !== 'undefined' && typeof CSS.escape === 'function' ? CSS.escape(id) : id;
}

function spanOf(width: LayoutWidth): string {
  return width === 'half' ? 'min-w-0' : 'min-w-0 lg:col-span-2';
}

export function LayoutGrid({ layout, render, gap = 'md', className }: LayoutGridProps): ReactElement {
  const gridRef = useRef<HTMLDivElement>(null);
  const pendingFocus = useRef<PendingFocus | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [drop, setDrop] = useState<Drop | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const trayLabelId = useId();

  const editing = layout.editing;
  const gapKey: Gap = gap === 'sm' ? 'sm' : 'md';

  // Restore focus to the control that acted, once the new order is in the DOM.
  useLayoutEffect(() => {
    const pending = pendingFocus.current;
    const grid = gridRef.current;
    if (!pending || !grid) return;
    pendingFocus.current = null;
    const id = escapeId(pending.id);
    const inBlock = (action: string) =>
      grid.querySelector<HTMLButtonElement>(
        `[data-layout-block="${id}"] [data-layout-action="${action}"]`,
      );
    const usable = (el: HTMLButtonElement | null) => (el && !el.disabled ? el : null);
    let target: HTMLButtonElement | null = null;
    if (pending.action === 'show') {
      target = grid.querySelector<HTMLButtonElement>(`[data-layout-show="${id}"]`);
    } else if (pending.action === 'up' || pending.action === 'down') {
      const other = pending.action === 'up' ? 'down' : 'up';
      target = usable(inBlock(pending.action)) ?? usable(inBlock(other)) ?? inBlock('hide');
    } else {
      target = inBlock(pending.action);
    }
    target?.focus();
  });

  /*
   * A repeated identical message is not re-announced by most screen readers,
   * so an alternating trailing no-break space makes each one a change.
   */
  const announce = (text: string) => {
    setAnnouncement((previous) => (previous === text ? `${text} ` : text));
  };

  const blocks = layout.blocks;
  const indexOf = (id: string) => blocks.findIndex((b) => b.id === id);

  const shown: { block: Block; node: ReactNode }[] = [];
  const hidden: Block[] = [];
  for (const block of blocks) {
    if (block.hidden) {
      // Only listed in the tray when the section would have something to show.
      if (editing && nodeOf(render[block.id]) !== null) hidden.push(block);
      continue;
    }
    const node = nodeOf(render[block.id]);
    if (node !== null) shown.push({ block, node });
  }

  const step = (block: Block, dir: -1 | 1) => {
    const at = shown.findIndex((entry) => entry.block.id === block.id);
    const neighbour = shown[at + dir];
    if (at < 0 || !neighbour) return;
    const to = indexOf(neighbour.block.id);
    if (to < 0) return;
    layout.move(block.id, to);
    pendingFocus.current = { id: block.id, action: dir < 0 ? 'up' : 'down' };
    announce(`${block.label} moved to position ${at + dir + 1} of ${shown.length}.`);
  };

  const toggleWidth = (block: Block) => {
    const next: LayoutWidth = block.width === 'half' ? 'full' : 'half';
    layout.setWidth(block.id, next);
    pendingFocus.current = { id: block.id, action: 'width' };
    announce(`${block.label} set to ${next} width.`);
  };

  const hide = (block: Block) => {
    layout.toggleHidden(block.id);
    pendingFocus.current = { id: block.id, action: 'show' };
    announce(`${block.label} hidden.`);
  };

  const show = (block: Block) => {
    layout.toggleHidden(block.id);
    pendingFocus.current = { id: block.id, action: 'hide' };
    announce(`${block.label} shown.`);
  };

  const clearDrag = () => {
    setDragId(null);
    setDrop(null);
  };

  const edgeFor = (event: DragEvent<HTMLElement>, el: HTMLElement): Edge => {
    const rect = el.getBoundingClientRect();
    const gridWidth = gridRef.current?.getBoundingClientRect().width ?? rect.width;
    // A block sharing its row reads left/right; a full-width one (or any
    // block in the one-column layout) reads above/below.
    if (rect.width < gridWidth * 0.75) {
      return event.clientX < rect.left + rect.width / 2 ? 'left' : 'right';
    }
    return event.clientY < rect.top + rect.height / 2 ? 'top' : 'bottom';
  };

  const onDragOver = (event: DragEvent<HTMLDivElement>, id: string) => {
    if (!dragId) return; // not our drag (a file, a column header…)
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    if (id === dragId) {
      if (drop) setDrop(null);
      return;
    }
    const edge = edgeFor(event, event.currentTarget);
    if (drop?.id !== id || drop.edge !== edge) setDrop({ id, edge });
  };

  const onDragLeave = (event: DragEvent<HTMLDivElement>, id: string) => {
    const next = event.relatedTarget;
    if (next instanceof Node && event.currentTarget.contains(next)) return;
    if (drop?.id === id) setDrop(null);
  };

  const onDrop = (event: DragEvent<HTMLDivElement>, target: Block) => {
    if (!dragId) return;
    event.preventDefault();
    if (target.id === dragId) {
      clearDrag();
      return;
    }
    const edge = drop?.id === target.id ? drop.edge : edgeFor(event, event.currentTarget);
    const from = indexOf(dragId);
    const at = indexOf(target.id);
    const dragged = from >= 0 ? blocks[from] : undefined;
    if (!dragged || at < 0) {
      clearDrag();
      return;
    }
    const slot = edge === 'top' || edge === 'left' ? at : at + 1;
    const to = slot > from ? slot - 1 : slot;
    if (to !== from) {
      layout.move(dragged.id, to);
      announce(`${dragged.label} moved.`);
    }
    clearDrag();
  };

  return (
    <div ref={gridRef} className={cx('grid min-w-0 grid-cols-1 lg:grid-cols-2', GAPS[gapKey], className)}>
      {shown.map(({ block, node }, i) => {
        if (!editing) {
          return (
            <div key={block.id} className={spanOf(block.width)}>
              {node}
            </div>
          );
        }
        const isDragged = dragId === block.id;
        const isTarget = drop?.id === block.id;
        return (
          <div
            key={block.id}
            data-layout-block={block.id}
            onDragOver={(event) => onDragOver(event, block.id)}
            onDragLeave={(event) => onDragLeave(event, block.id)}
            onDrop={(event) => onDrop(event, block)}
            className={cx(
              spanOf(block.width),
              'relative rounded-lg border border-dashed p-2',
              'transition-[opacity,border-color,background-color] duration-150 motion-reduce:transition-none',
              isTarget ? 'border-accent-brand bg-accent-brand-tint' : 'border-hairline-strong',
              isDragged && 'opacity-50',
            )}
          >
            {isTarget && drop && <DropLine edge={drop.edge} gap={gapKey} />}
            <BlockHeader
              block={block}
              first={i === 0}
              last={i === shown.length - 1}
              onUp={() => step(block, -1)}
              onDown={() => step(block, 1)}
              onWidth={() => toggleWidth(block)}
              onHide={() => hide(block)}
              onDragStart={(event) => {
                const frame = event.currentTarget.closest('[data-layout-block]');
                event.dataTransfer.effectAllowed = 'move';
                event.dataTransfer.setData('text/plain', block.id);
                if (frame instanceof HTMLElement) {
                  const rect = frame.getBoundingClientRect();
                  event.dataTransfer.setDragImage(frame, event.clientX - rect.left, event.clientY - rect.top);
                }
                setDragId(block.id);
              }}
              onDragEnd={clearDrag}
            />
            {node}
          </div>
        );
      })}

      {editing && hidden.length > 0 && (
        <div
          role="group"
          aria-labelledby={trayLabelId}
          className="flex min-w-0 flex-wrap items-center gap-2 border-t border-hairline pt-3 lg:col-span-2"
        >
          <span id={trayLabelId} className="text-xs font-semibold text-fg-muted">
            Hidden sections
          </span>
          {hidden.map((block) => (
            <Button
              key={block.id}
              variant="secondary"
              size="sm"
              data-layout-show={block.id}
              aria-label={`Show ${block.label}`}
              leadingIcon={<EyeIcon />}
              onClick={() => show(block)}
              className="max-w-full"
            >
              <span className="truncate">{block.label}</span>
            </Button>
          ))}
        </div>
      )}

      <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        {announcement}
      </div>
    </div>
  );
}

interface BlockHeaderProps {
  block: Block;
  first: boolean;
  last: boolean;
  onUp: () => void;
  onDown: () => void;
  onWidth: () => void;
  onHide: () => void;
  onDragStart: (event: DragEvent<HTMLSpanElement>) => void;
  onDragEnd: () => void;
}

function BlockHeader({ block, first, last, onUp, onDown, onWidth, onHide, onDragStart, onDragEnd }: BlockHeaderProps) {
  const half = block.width === 'half';
  const label = block.label;
  return (
    <div className="mb-2 flex min-w-0 flex-wrap items-center gap-1 rounded-md bg-surface-sunken p-1">
      <div className="flex min-w-0 flex-1 basis-32 items-center gap-1">
        <Tooltip label={`Drag ${label}`}>
          <span
            role="img"
            draggable
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            aria-label={`Drag ${label}`}
            className="flex h-8 w-6 shrink-0 cursor-grab items-center justify-center rounded-md text-fg-muted transition-colors hover:bg-surface-hover hover:text-fg active:cursor-grabbing max-sm:h-10"
          >
            <GripIcon />
          </span>
        </Tooltip>
        <span className="min-w-0 flex-1 truncate text-xs font-semibold text-fg">{label}</span>
      </div>
      <div className="ml-auto flex shrink-0 items-center gap-0.5">
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          data-layout-action="up"
          aria-label={`Move ${label} up`}
          tooltip={first ? `Move ${label} up: already first` : undefined}
          disabled={first}
          onClick={onUp}
          leadingIcon={<ArrowUpIcon />}
        />
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          data-layout-action="down"
          aria-label={`Move ${label} down`}
          tooltip={last ? `Move ${label} down: already last` : undefined}
          disabled={last}
          onClick={onDown}
          leadingIcon={<ArrowDownIcon />}
        />
        <span aria-hidden="true" className="mx-0.5 h-4 w-px bg-hairline-strong max-lg:hidden" />
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          data-layout-action="width"
          aria-label={half ? `Full width for ${label}` : `Half width for ${label}`}
          onClick={onWidth}
          leadingIcon={half ? <FullWidthIcon /> : <HalfWidthIcon />}
          className="max-lg:hidden"
        />
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          data-layout-action="hide"
          aria-label={`Hide ${label}`}
          onClick={onHide}
          leadingIcon={<EyeOffIcon />}
        />
      </div>
    </div>
  );
}

function DropLine({ edge, gap }: { edge: Edge; gap: Gap }) {
  return (
    <span
      aria-hidden="true"
      className={cx('pointer-events-none absolute z-10 rounded-full bg-accent-brand', EDGES[gap][edge])}
    />
  );
}

/*
 * Lucide paths, inlined here (not in `catalog/icons.tsx`) so this component
 * owns its glyphs. Same stroke and grid as the shared set.
 */
const stroke = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
  className: 'h-4 w-4 shrink-0',
} as const;

function GripIcon() {
  return (
    <svg {...stroke}>
      <circle cx="9" cy="12" r="1" />
      <circle cx="9" cy="5" r="1" />
      <circle cx="9" cy="19" r="1" />
      <circle cx="15" cy="12" r="1" />
      <circle cx="15" cy="5" r="1" />
      <circle cx="15" cy="19" r="1" />
    </svg>
  );
}

function ArrowUpIcon() {
  return (
    <svg {...stroke}>
      <path d="m5 12 7-7 7 7" />
      <path d="M12 19V5" />
    </svg>
  );
}

function ArrowDownIcon() {
  return (
    <svg {...stroke}>
      <path d="M12 5v14" />
      <path d="m19 12-7 7-7-7" />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg {...stroke}>
      <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg {...stroke}>
      <path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49" />
      <path d="M14.084 14.158a3 3 0 0 1-4.242-4.242" />
      <path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143" />
      <path d="m2 2 20 20" />
    </svg>
  );
}

/** columns-2: offered on a full block, to make it half. */
function HalfWidthIcon() {
  return (
    <svg {...stroke}>
      <rect width="18" height="18" x="3" y="3" rx="2" />
      <path d="M12 3v18" />
    </svg>
  );
}

/** rectangle-horizontal: offered on a half block, to make it full. */
function FullWidthIcon() {
  return (
    <svg {...stroke}>
      <rect width="20" height="12" x="2" y="6" rx="2" />
    </svg>
  );
}
